const ua = () => navigator.userAgent

/**
 * 当前设备是否拥有此内核或系统
 * @param {string} name - 内核或系统名称
 * @param {boolean} caseSensitive - 区分大小写
 * @returns {boolean} 判断结果
 */
export const judgeUa = (name, caseSensitive = true) => {
  const userAgent = ua()
  const currentAgent = caseSensitive ? userAgent : userAgent.toLowerCase()
  const idx = currentAgent.indexOf(name)
  return idx > -1
}

/**
 * 当前设备是否拥有此内核或系统(小写)
 * @param {string} name - 内核或系统名称
 * @returns {boolean} 判断结果
 */
const handleJudgeUa = name => judgeUa(name, false)

/**
   * 处理浏览器版本
   * @param {RegExp} reg  正则
   * @returns {string} 浏览器版本
   */
const handleBrowserVersion = reg => ua().toLowerCase().match(reg)[1] || 'Unknown'

/**
 * 当前设备是否为【移动端】
 * @returns {boolean} 判断结果
 */
/* eslint-disable-next-line max-len */
export const isMobile = () => /Android|iPhone|iPad|iPod|BlackBerry|webOS|Windows Phone|SymbianOS|IEMobile|Opera Mini/i.test(ua())

/**
 * 当前设备是否为【PC端】
 * @returns {boolean} 判断结果
 */
export const isPc = () => {
  const agents = ['Android', 'iPhone', 'SymbianOS', 'Windows Phone', 'iPad', 'iPod']
  const result = agents.some(e => judgeUa(e))
  return !result
}

/**
 * 当前设备系统是否为【安卓】
 * @returns {boolean} 判断结果
 */
export const isAndroid = () => judgeUa('Android') || judgeUa('Adr')

/**
 * 当前设备系统是否为【ios】
 * @returns {boolean} 判断结果
 */
export const isIOS = () => !!ua().match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)

/**
 * 当前设备是否为【ipad】
 * @returns {boolean} 判断结果
 */
export const isIpad = () => judgeUa('iPad')

/**
 * 当前设备是否为【iphone】
 * @returns {boolean} 判断结果
 */
export const isIphone = () => judgeUa('iPhone')

/**
 * 当前设备环境是否为【QQ】
 * @returns {boolean} 判断结果
 */
export const isQQ = () => /QQ/i.test(ua())

/**
 * 当前设备环境是否为【微信】
 * @returns {boolean} 判断结果
 */
export const isWechat = () => /MicroMessenger/i.test(ua())

/**
 * 当前设备环境是否为【支付宝】
 * @returns {boolean} 判断结果
 */
export const isAlipay = () => /Alipay/i.test(ua())

/**
 * 当前设备【IE浏览器】版本
 * @returns {number} 具体版本（不是ie则为-1， IE版本过低为0）
 */
/* eslint-disable no-magic-numbers */
export const ieVersion = () => {
  const userAgent = ua()
  const isIE = judgeUa('compatible') && judgeUa('MSIE')
  const isIE11 = userAgent.match(/(trident)\/([\d.]+)/i)
  if (!isIE && isIE11) {
    return 11
  }

  const matches = /MSIE (\d+\.\d+);/.exec(userAgent)
  if (!matches) {
    return -1
  }
  const fIEVersion = parseFloat(matches[1])

  if (fIEVersion >= 7 && fIEVersion <= 10) {
    return fIEVersion
  }
  return 0
}
/* eslint-enable no-magic-numbers */

/**
 * 获取操作系统信息
 * @returns {object} 操作系统集合
 */
export const getOsInfo = () => {
  const winMap = {
    'windows nt 5.0': 'Windows 2000',
    'windows nt 5.1': 'Windows XP',
    'windows nt 5.2': 'Windows XP',
    'windows nt 6.0': 'Windows Vista',
    'windows nt 6.1': 'Windows 7',
    'windows 7': 'Windows 7',
    'windows nt 6.2': 'Windows 8',
    'windows 8': 'Windows 8',
    'windows nt 6.3': 'Windows 8.1',
    'windows nt 6.4': 'Windows 10',
    'windows nt 10.0': 'Windows 10',
  }

  const systemMap = {
    win: 'Windows',
    ipad: 'Ios',
    iphone: 'Ios',
    android: 'Android',
    x11: 'Unix',
    unix: 'Unix',
    sunname: 'Unix',
    bsd: 'Unix',
    linux: 'Linux',
    mac: 'Mac'
  }

  let sver = 'Unknown'
  const systemKey = Object.keys(systemMap).find(p => handleJudgeUa(p))
  const system = systemMap[systemKey] || 'Unknown'

  if (system === 'Windows') {
    const winKey = Object.keys(winMap).find(c => handleJudgeUa(c))
    sver = winMap[winKey] || 'Unknown'
  }

  return { system, sver }
}

/**
 * 获取当前浏览器名称和版本
 * @returns {object} 浏览器信息集合
 */
export const getBrowser = () => {
  const ieVer = ieVersion()
  if (ieVer >= 0) {
    const browser = 'IE'
    const bver = ieVer.toString()
    return { browser, bver }
  }

  const isChrome = judgeUa('Chrome') && judgeUa('Safari') && !judgeUa('Edge')
  if (isChrome) {
    const browser = 'Chrome'
    const bver = handleBrowserVersion(/chrome\/([\d.]+)/)
    return { browser, bver }
  }

  const isFirefox = judgeUa('Firefox')
  if (isFirefox) {
    const browser = 'Firefox'
    const bver = handleBrowserVersion(/firefox\/([\d.]+)/)
    return { browser, bver }
  }

  const isOpera = judgeUa('Opera')
  if (isOpera) {
    const browser = 'Opera'
    const bver = handleBrowserVersion(/opera.([\d.]+)/)
    return { browser, bver }
  }

  const isSafari = judgeUa('Safari') && !judgeUa('Chrome')
  if (isSafari) {
    const browser = 'Safari'
    const bver = handleBrowserVersion(/version\/([\d.]+)/)
    return { browser, bver }
  }

  const isEdge = judgeUa('Edge')
  if (isEdge) {
    const browser = 'Edge'
    const bver = handleBrowserVersion(/edge\/([\d.]+)/)
    return { browser, bver }
  }

  return { browser: 'Unknown', bver: 'Unknown' }
}

/**
 * 获取设备信息集合
 * @returns {object} 浏览器名称
 */
export const getDeviceInfo = () => ({ ...getOsInfo(), ...getBrowser() })
